/* ═══════════════════════════════════════════════════════════════
   REVIEW — the paint (docs/SYNC_PLAN.md §2.3, guarantee 1).

   One ProseMirror plugin, shared by every Review entrance (the project
   spec's Master updates, the master's self-review before Publish). It is
   fed `{ changes, decisions, currentId }` and paints each Change onto the
   document it already belongs to:

     · removed words        → struck inline decoration over the copy's words
     · added words          → a widget carrying upstream's new words
     · changed words        → struck + added, in place; on a true conflict
                              the copy's own words stay plain between them
     · removed block        → the whole block struck (node decoration)
     · added block          → a ghost block after its anchor
     · moved / formatting   → a small label above the block
     · reconciled           → nothing (the panel's receipt line covers it)

   Decided items: accepted → a plain preview of the result; rejected →
   faded. The current change wears a ring.

   The document is NEVER mutated here — decorations only. Positions are
   found by block id (+ table cell), then word-token offsets are mapped to
   characters (`tokenOffsetToPos`) and characters to document positions.

   `invert` serves the self-review walk (draft vs published): the changes
   were computed with the DOCUMENT ON SCREEN as ours and the published text
   as theirs, so every kind reads backwards — upstream "removed" words are
   words the draft added, and so on.
   ═══════════════════════════════════════════════════════════════ */

import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Change, Decision } from '../../shared/sync';
import { tokenize } from '../../shared/sync/tokens';
import type { ContentBlock } from '../../shared/types';

export interface ReviewPluginInput {
  /** Every change on the row — reconciled ones are skipped. */
  changes: Change[];
  decisions: Record<string, Decision>;
  currentId: string | null;
  /** Self-review: the document on screen is ours, the reference is theirs. */
  invert?: boolean;
  /** Where an added paragraph comes from, in words (the Revert lane:
   *  "version 3"). Unset = "the master". */
  source?: string;
}

interface ReviewPluginState {
  input: ReviewPluginInput | null;
  deco: DecorationSet;
}

export const reviewPluginKey = new PluginKey<ReviewPluginState>('sfReview');

/* ─── Word tokens → characters ─── */

/**
 * Word-token offsets [start, end) of `nodeText` → character offsets
 * [from, to). Tokens are `shared/sync/tokens.ts` words (whitespace splits),
 * so punctuation rides with its word and runs of spaces are one gap.
 *
 * An empty range (start === end) is an insertion point: the first character
 * of word `start`, or the end of the last word when `start` is past it.
 */
export function tokenOffsetToPos(
  nodeText: string,
  start: number,
  end: number
): { from: number; to: number } {
  const t = tokenize(nodeText);
  const n = t.words.length;
  const starts: number[] = [];
  let at = 0;
  for (let i = 0; i < n; i++) {
    at += t.gaps[i].length;
    starts.push(at);
    at += t.words[i].length;
  }
  const wordEnd = (i: number): number => starts[i] + t.words[i].length;
  const s = Math.max(0, Math.min(start, n));
  const e = Math.max(s, Math.min(end, n));
  if (n === 0) {
    const p = t.gaps[0].length;
    return { from: p, to: p };
  }
  if (s === e) {
    const p = s < n ? starts[s] : wordEnd(n - 1);
    return { from: p, to: p };
  }
  return { from: starts[s], to: wordEnd(e - 1) };
}

/* ─── Characters → document positions ─── */

/** The text of one container (a paragraph, or a table cell whose paragraphs
 *  join with a newline — the same text the storage format holds) and the
 *  document position of every character in it. */
export interface TextMap {
  text: string;
  posAt: (i: number) => number;
}

function textMapOf(node: PMNode, nodePos: number, isCell: boolean): TextMap {
  let text = '';
  const positions: number[] = [];
  let end = nodePos + 1;
  const addInline = (para: PMNode, contentStart: number): void => {
    para.forEach((child, offset) => {
      if (!child.isText || !child.text) return;
      for (let j = 0; j < child.text.length; j++) positions.push(contentStart + offset + j);
      text += child.text;
    });
    end = contentStart + para.content.size;
  };
  if (isCell) {
    let first = true;
    node.forEach((para, offset) => {
      const contentStart = nodePos + 1 + offset + 1;
      if (!first) {
        positions.push(end);
        text += '\n';
      }
      first = false;
      addInline(para, contentStart);
    });
  } else {
    addInline(node, nodePos + 1);
  }
  return { text, posAt: (i) => (i < positions.length ? positions[i] : end) };
}

/** Word-token range → document range inside one container. */
export function docRangeOf(map: TextMap, start: number, end: number): { from: number; to: number } {
  const c = tokenOffsetToPos(map.text, start, end);
  const from = map.posAt(c.from);
  const to = c.to > c.from ? map.posAt(c.to - 1) + 1 : from;
  return { from, to };
}

interface Located {
  node: PMNode;
  pos: number;
}

function topBlocks(doc: PMNode): Map<string, Located> {
  const out = new Map<string, Located>();
  doc.forEach((node, pos) => {
    const id = node.attrs?.blockId as string | undefined;
    if (id && !out.has(id)) out.set(id, { node, pos });
  });
  return out;
}

function cellOf(table: Located, row: number, col: number): Located | null {
  if (row >= table.node.childCount) return null;
  let rowPos = table.pos + 1;
  for (let r = 0; r < row; r++) rowPos += table.node.child(r).nodeSize;
  const rowNode = table.node.child(row);
  if (col >= rowNode.childCount) return null;
  let cellPos = rowPos + 1;
  for (let c = 0; c < col; c++) cellPos += rowNode.child(c).nodeSize;
  return { node: rowNode.child(col), pos: cellPos };
}

/** The text container a word-run change lives in, or null when the block
 *  (or cell) is not in the document any more. */
export function containerFor(
  doc: PMNode,
  change: Pick<Change, 'blockId' | 'cell'>,
  blocks: Map<string, Located> = topBlocks(doc)
): TextMap | null {
  const b = blocks.get(change.blockId);
  if (!b) return null;
  if (change.cell) {
    const cell = cellOf(b, change.cell.row, change.cell.col);
    return cell ? textMapOf(cell.node, cell.pos, true) : null;
  }
  if (b.node.type.name !== 'specParagraph') return null;
  return textMapOf(b.node, b.pos, false);
}

/* ─── Widgets ─── */

type DecisionState = 'open' | 'accepted' | 'rejected';

function stateOf(c: Change, decisions: Record<string, Decision>): DecisionState {
  const d = decisions[c.id];
  return d === 'accept' ? 'accepted' : d === 'reject' ? 'rejected' : 'open';
}

function cls(base: string, state: DecisionState, current: boolean): string {
  return [base, state !== 'open' ? `is-${state}` : '', current ? 'sf-review-current' : '']
    .filter(Boolean)
    .join(' ');
}

function wordsWidget(
  text: string,
  className: string,
  label: string,
  changeId: string
): () => HTMLElement {
  return () => {
    const span = document.createElement('span');
    span.className = className;
    span.setAttribute('contenteditable', 'false');
    span.setAttribute('data-review-change', changeId);
    span.setAttribute('aria-label', `${label}: ${text.trim()}`);
    span.textContent = text;
    return span;
  };
}

function ghostBlockWidget(
  block: ContentBlock | undefined,
  text: string,
  className: string,
  label: string,
  changeId: string
): () => HTMLElement {
  return () => {
    const div = document.createElement('div');
    div.className = className;
    div.setAttribute('contenteditable', 'false');
    div.setAttribute('data-review-change', changeId);
    div.setAttribute('aria-label', label);
    const level = Math.max(1, Math.min(9, block?.level || 1));
    div.style.setProperty('--sf-review-level', String(level - 1));
    div.textContent =
      block?.blockType === 'table'
        ? text || 'Table'
        : block?.blockType === 'image'
          ? text || 'Figure'
          : text || ' ';
    return div;
  };
}

function tagWidget(text: string, className: string, changeId: string): () => HTMLElement {
  return () => {
    const div = document.createElement('div');
    div.className = className;
    div.setAttribute('contenteditable', 'false');
    div.setAttribute('data-review-change', changeId);
    const tag = document.createElement('span');
    tag.className = 'sf-review-tag';
    tag.textContent = text;
    div.appendChild(tag);
    return div;
  };
}

/* ─── Change → decorations ─── */

export function buildReviewDecorations(doc: PMNode, input: ReviewPluginInput | null): Decoration[] {
  if (!input) return [];
  const out: Decoration[] = [];
  const blocks = topBlocks(doc);
  const invert = !!input.invert;
  // Ghost blocks landing on the same spot keep upstream's order.
  const sidesAt = new Map<number, number>();
  const nextSide = (pos: number): number => {
    const s = (sidesAt.get(pos) ?? 0) + 1;
    sidesAt.set(pos, s);
    return s;
  };
  const landingPos = (c: Change): number => {
    if (!c.anchor?.after) return 0;
    const a = blocks.get(c.anchor.after);
    return a ? a.pos + a.node.nodeSize : doc.content.size;
  };

  for (const c of input.changes) {
    if (c.kind === 'reconciled') continue;
    const st = stateOf(c, input.decisions);
    const cur = c.id === input.currentId;
    const key = `${c.id}:${st}:${cur ? 1 : 0}`;

    if (c.whole) {
      if (c.kind === 'added') {
        // Not inverted: upstream's new block, where it would land.
        // Inverted: the published block this draft no longer has.
        const at = landingPos(c);
        out.push(
          Decoration.widget(
            at,
            ghostBlockWidget(
              c.block,
              c.after,
              cls(
                invert
                  ? 'sf-review-ghost sf-review-removed-block'
                  : 'sf-review-ghost sf-review-added-block',
                st,
                cur
              ),
              invert
                ? 'Paragraph removed in this draft'
                : `New paragraph from ${input.source ?? 'the master'}`,
              c.id
            ),
            { side: nextSide(at), key, ignoreSelection: true }
          )
        );
        continue;
      }
      const b = blocks.get(c.blockId);
      if (!b) continue;
      if (c.kind === 'removed') {
        // Inverted: a block the draft added.
        out.push(
          Decoration.node(b.pos, b.pos + b.node.nodeSize, {
            class: cls(invert ? 'sf-review-added-block' : 'sf-review-removed-block', st, cur),
            'data-review-change': c.id,
          })
        );
        continue;
      }
      // moved, or whole changed (formatting / structure / image).
      const moved = c.kind === 'moved';
      const label = moved
        ? st === 'accepted'
          ? 'Moves when you finish'
          : 'Moved'
        : st === 'accepted'
          ? 'Formatting updates when you finish'
          : 'Formatting changed';
      out.push(
        Decoration.widget(b.pos, tagWidget(label, cls('sf-review-tagline', st, cur), c.id), {
          side: -3,
          key: `${key}:tag`,
          ignoreSelection: true,
        }),
        Decoration.node(b.pos, b.pos + b.node.nodeSize, {
          class: cls(moved ? 'sf-review-moved' : 'sf-review-changed-block', st, cur),
          'data-review-change': c.id,
        })
      );
      continue;
    }

    // Word runs.
    if (!c.range) continue;
    const map = containerFor(doc, c, blocks);
    if (!map) continue;
    const r = docRangeOf(map, c.range.start, c.range.end);
    const n = tokenize(map.text).words.length;
    const atEnd = c.range.start >= n && n > 0;
    // Spacing for a widget that sits at an insertion point.
    const padded = (text: string): string => (atEnd ? ' ' + text : text + ' ');
    const inline = (base: string, from: number, to: number): void => {
      if (to <= from) return;
      out.push(
        Decoration.inline(from, to, {
          class: cls(base, st, cur),
          'data-review-change': c.id,
        })
      );
    };
    const widget = (
      base: string,
      label: string,
      at: number,
      text: string,
      side: number,
      keySuffix: string
    ): void => {
      if (!text.trim()) return;
      out.push(
        Decoration.widget(at, wordsWidget(text, cls(base, st, cur), label, c.id), {
          side,
          key: `${key}:${keySuffix}`,
          ignoreSelection: true,
        })
      );
    };

    if (!invert) {
      if (c.kind === 'added') {
        widget('sf-review-added', 'Added', r.from, padded(c.after), atEnd ? 1 : -1, 'add');
      } else if (c.kind === 'removed') {
        inline('sf-review-removed', r.from, r.to);
      } else if (c.oursText !== undefined) {
        // A true conflict: the base words struck, the copy's own words
        // plain, upstream's words added — nothing is auto-picked.
        // Accepted: Finish replaces the copy's words with upstream's, so the
        // preview hides them too — what is shown is what Finish applies.
        widget('sf-review-removed', 'Removed', r.from, c.before + ' ', -1, 'base');
        if (st === 'accepted') inline('sf-review-removed', r.from, r.to);
        widget(
          'sf-review-added',
          'Added',
          r.to,
          (r.to > r.from && st !== 'accepted' ? ' ' : '') + c.after,
          1,
          'add'
        );
      } else {
        inline('sf-review-removed', r.from, r.to);
        widget('sf-review-added', 'Added', r.to, ' ' + c.after, 1, 'add');
      }
    } else if (c.kind === 'added') {
      // Self-review: words the published text has and this draft dropped.
      widget('sf-review-removed', 'Removed', r.from, padded(c.after), atEnd ? 1 : -1, 'del');
    } else if (c.kind === 'removed') {
      // Words this draft added.
      inline('sf-review-added', r.from, r.to);
    } else {
      widget('sf-review-removed', 'Removed', r.from, c.after + ' ', -1, 'del');
      inline('sf-review-added', r.from, r.to);
    }
  }
  return out;
}

function build(state: EditorState, input: ReviewPluginInput | null): DecorationSet {
  const decos = buildReviewDecorations(state.doc, input);
  return decos.length ? DecorationSet.create(state.doc, decos) : DecorationSet.empty;
}

/** The plugin on its own — the hosts register it on their live editor while
 *  Review is open and unregister it when they leave. */
export function reviewPlugin(initial: ReviewPluginInput | null = null): Plugin<ReviewPluginState> {
  return new Plugin<ReviewPluginState>({
    key: reviewPluginKey,
    state: {
      init: (_config, state) => ({ input: initial, deco: build(state, initial) }),
      apply: (tr: Transaction, value, _old, newState) => {
        const meta = tr.getMeta(reviewPluginKey) as { input: ReviewPluginInput | null } | undefined;
        if (meta) return { input: meta.input, deco: build(newState, meta.input) };
        // A peer's edit while reviewing: positions are re-derived by id.
        if (tr.docChanged) return { input: value.input, deco: build(newState, value.input) };
        return value;
      },
    },
    props: {
      decorations: (state) => reviewPluginKey.getState(state)?.deco ?? DecorationSet.empty,
    },
  });
}

/** A meta-only transaction that hands the plugin new input. Never touches
 *  the document, never enters undo history. */
export function setReviewInput(tr: Transaction, input: ReviewPluginInput | null): Transaction {
  return tr.setMeta(reviewPluginKey, { input }).setMeta('addToHistory', false);
}
